import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { createBrowserHistory } from "history"
import * as R from "ramda"
import { createReduxHistoryContext } from "redux-first-history"
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from "redux-persist"
import storage from "redux-persist/lib/storage" // defaults to localStorage for web

import rootReducer from "./rootReducer"
import { syncIncognitoActive } from "./Utils/incognito"

const PERSISTED_REDUCER_VERSION = 3 // Bump this value to reset persisted reducer (doesn't clear nested persisted reducers)

// Opt-in, not opt-out: every other slice either owns its own nested `persistReducer` (the
// preferred pattern — own key/version/migrate, so it can bump without purging the whole store)
// or is server-backed and not worth persisting. A new slice is only saved here if named below.
const persistConfig = {
  key: "chargeback",
  version: PERSISTED_REDUCER_VERSION,
  storage,
  whitelist: ["creditAutomation", "creditsAutomation"],
  migrate: (state) => {
    if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
      return Promise.resolve(undefined)
    }
    // The whitelist only filters writes, so keys left behind by slices that have since moved to
    // their own persistReducer would otherwise rehydrate once on top of their new shape.
    return Promise.resolve(state && R.pick(persistConfig.whitelist, state))
  }
}

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory()
})

export default async () => {
  const persistedReducer = persistReducer(
    persistConfig,
    combineReducers({
      ...rootReducer,
      router: routerReducer
    })
  )

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
        },
        // Dev-only deep freeze, off for dev performance: the state tree holds large paginated
        // tables. Safe to revisit now that nothing in it is backed by seamless-immutable.
        immutableCheck: false
      }).concat(routerMiddleware),
    devTools: process.env.NODE_ENV !== "production"
  })

  // Keep the non-React mirror of Incognito mode in sync (chart callbacks read it, see Utils/incognito).
  syncIncognitoActive(store.getState())
  store.subscribe(() => syncIncognitoActive(store.getState()))

  const history = createReduxHistory(store)

  const persistor = await persistStore(store)

  return {
    store,
    persistor,
    history
  }
}
